import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  CSP_NONCE,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  withDisabledInitialNavigation,
} from '@angular/router';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { provideTransloco } from '@jsverse/transloco';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import {
  NgxsRouterPluginModule,
  RouterStateSerializer,
} from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { Apollo, APOLLO_NAMED_OPTIONS, NamedOptions } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';

import {
  GlobalDialogService,
  LoggingService,
  spinnerInterceptor,
  SpinnerService,
} from '@customer-portal/core';
import { UnreadActionsState } from '@customer-portal/data-access/actions/state/unread-actions.state';
import { FeatureAnnouncementState } from '@customer-portal/data-access/feature-announcements';
import { GlobalState } from '@customer-portal/data-access/global/state/global.state';
import { QualtricsSurveyFeedbackState } from '@customer-portal/data-access/global/state/qualtrics-survey-feedback.state';
import { UnsavedFiltersState } from '@customer-portal/data-access/global/state/unsaved-filters.state';
import { UnreadNotificationsState } from '@customer-portal/data-access/notifications/state/unread-notifications.state';
import { SettingsState } from '@customer-portal/data-access/settings/state/settings.state';
import { environment } from '@customer-portal/environments';
import {
  FEATURE_TOUR_READINESS_GATEWAY,
  NgxsFeatureTourReadinessGateway,
} from '@customer-portal/features/feature-announcements';
import { OverviewSharedState } from '@customer-portal/overview-shared';
import {
  appInitializer,
  loggingInitializer,
} from '@customer-portal/permissions';
import { PreferenceState } from '@customer-portal/preferences/state/preference.state';
import { CustomRouterStateSerializer } from '@customer-portal/router';
import { ENGLISH_LANGUAGE_CODE } from '@customer-portal/shared/models';
import { CustomTitleStrategy } from '@customer-portal/shared/services/custom-title-strategy';
import {
  LocaleService,
  registerLocales,
} from '@customer-portal/shared/services/locale';

import {
  APOLLO_NAMED_CLIENTS,
  createApolloNamedClientConfig,
  getGraphqlHostForClient,
} from './components/helpers/app.config.helpers';
import DNVPreset from './theme/dnv-presets';
import { appRoutes } from './app.routes';
import {
  customHeaderInterceptor,
  errorInterceptor,
  GlobalErrorHandler,
  maintenanceCancellationInterceptor,
} from './interceptors';
import { TranslocoHttpLoader } from './transloco-http.loader';

declare global {
  /* eslint-disable no-var, vars-on-top */
  var dnvRandomNonce: string;
}

const errorLink = new ErrorLink(({ error }) => {
  const loggingService = new LoggingService();

  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) =>
      loggingService.logTrace(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  } else {
    loggingService.logTrace(`[Network error]: ${error}`);
  }
});

registerLocales();

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    {
      provide: FEATURE_TOUR_READINESS_GATEWAY,
      useClass: NgxsFeatureTourReadinessGateway,
    },
    provideAppInitializer(loggingInitializer),
    provideHttpClient(
      withInterceptors([
        customHeaderInterceptor,
        errorInterceptor,
        spinnerInterceptor,
        maintenanceCancellationInterceptor,
      ]),
    ),

    provideAppInitializer(appInitializer),
    provideRouter(appRoutes, withDisabledInitialNavigation()),
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    GlobalDialogService,
    { provide: DialogService, useExisting: GlobalDialogService },
    ConfirmationService,
    provideHttpClient(),
    providePrimeNG({
      theme: {
        preset: DNVPreset,
        options: {
          darkModeSelector: 'none',
          cssLayer: {
            name: 'primeng',
            order: 'primeng, customer-portal-styles',
          },
        },
      },
    }),
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          UnreadActionsState,
          PreferenceState,
          SettingsState,
          OverviewSharedState,
          UnreadNotificationsState,
          GlobalState,
          UnsavedFiltersState,
          QualtricsSurveyFeedbackState,
          FeatureAnnouncementState,
        ],
        {
          developmentMode: !environment.production,
          compatibility: {
            strictContentSecurityPolicy: true,
          },
        },
      ),
      NgxsDispatchPluginModule.forRoot(),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsRouterPluginModule.forRoot(),
    ),
    {
      provide: CSP_NONCE,
      useValue: globalThis.dnvRandomNonce,
    },
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: [ENGLISH_LANGUAGE_CODE],
        defaultLang: ENGLISH_LANGUAGE_CODE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        fallbackLang: ENGLISH_LANGUAGE_CODE,
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService): string =>
        localeService.getLocale(),
      deps: [LocaleService],
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(httpLink: HttpLink): NamedOptions {
        const options: NamedOptions = {};

        APOLLO_NAMED_CLIENTS.forEach((name) => {
          const uri = getGraphqlHostForClient(name);
          options[name] = createApolloNamedClientConfig(
            uri,
            httpLink,
            errorLink,
          );
        });

        return options;
      },
      deps: [HttpLink],
    },
    Apollo,
    SpinnerService,
    MessageService,
  ],
};
